import { useState } from 'react';
import { Copy, Check, ChevronDown, ChevronRight } from 'lucide-react';

interface TemplateVariable {
  name: string;
  key: string;
  description: string;
  example: string;
  required: boolean;
  dataType: string;
  category?: string;
}

interface VariableReferencePanelProps {
  variables: TemplateVariable[];
}

export function VariableReferencePanel({ variables }: VariableReferencePanelProps) {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(['recipient', 'task', 'happening', 'organization'])
  );

  // Group variables by category
  const variablesByCategory = variables.reduce((acc, variable) => {
    const category = variable.category || 'other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(variable);
    return acc;
  }, {} as Record<string, TemplateVariable[]>);

  const categories = Object.keys(variablesByCategory).sort((a, b) => {
    // Sort categories: recipient, task, happening, organization, others alphabetically
    const order = ['recipient', 'task', 'happening', 'profile', 'organization', 'branding', 'related'];
    const aIndex = order.indexOf(a);
    const bIndex = order.indexOf(b);
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    return a.localeCompare(b);
  });

  const handleCopy = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const filteredVariables = searchTerm
    ? variables.filter(
        (v) =>
          v.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          v.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          v.key.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : null;

  return (
    <div className="border rounded-lg bg-card">
      <div className="p-4 border-b bg-muted/30">
        <h3 className="font-semibold mb-2">Available Variables</h3>
        <p className="text-sm text-muted-foreground mb-3">
          Click to copy variable keys. Use them in your templates like <code className="bg-muted px-1 py-0.5 rounded text-xs">{'{{variableName}}'}</code>
        </p>
        <input
          type="text"
          placeholder="Search variables..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 border rounded-md text-sm"
        />
      </div>

      <div className="max-h-96 overflow-y-auto">
        {filteredVariables ? (
          // Search results
          <div className="divide-y">
            {filteredVariables.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                No variables found matching "{searchTerm}"
              </div>
            ) : (
              filteredVariables.map((variable) => (
                <VariableItem
                  key={variable.key}
                  variable={variable}
                  copiedKey={copiedKey}
                  onCopy={handleCopy}
                />
              ))
            )}
          </div>
        ) : (
          // Grouped by category
          <div className="divide-y">
            {categories.map((category) => {
              const isExpanded = expandedCategories.has(category);
              const categoryVars = variablesByCategory[category];

              return (
                <div key={category}>
                  <button
                    onClick={() => toggleCategory(category)}
                    className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      )}
                      <span className="font-medium capitalize">{category}</span>
                      <span className="text-xs text-muted-foreground">({categoryVars.length})</span>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="bg-muted/20">
                      {categoryVars.map((variable) => (
                        <VariableItem
                          key={variable.key}
                          variable={variable}
                          copiedKey={copiedKey}
                          onCopy={handleCopy}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

interface VariableItemProps {
  variable: TemplateVariable;
  copiedKey: string | null;
  onCopy: (key: string) => void;
}

function VariableItem({ variable, copiedKey, onCopy }: VariableItemProps) {
  const isCopied = copiedKey === variable.key;

  return (
    <button
      onClick={() => onCopy(variable.key)}
      className="w-full text-left p-3 hover:bg-muted/50 transition-colors group"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <code className="text-sm font-mono bg-muted px-2 py-0.5 rounded">
              {variable.key}
            </code>
            {variable.required && (
              <span className="text-xs bg-error-muted text-error-foreground px-1.5 py-0.5 rounded">
                Required
              </span>
            )}
            <span className="text-xs text-muted-foreground capitalize">
              {variable.dataType}
            </span>
          </div>
          <p className="text-sm text-muted-foreground">{variable.description}</p>
          <p className="text-xs text-muted-foreground mt-1">
            Example: <span className="font-mono">{variable.example}</span>
          </p>
        </div>

        <div className="flex-shrink-0">
          {isCopied ? (
            <Check className="h-4 w-4 text-success" />
          ) : (
            <Copy className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
          )}
        </div>
      </div>
    </button>
  );
}
